import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchBhajanDataRequest, selectCatalog } from '../Features/BhajanSlice';
import { RootState, AppDispatch } from '../store/store';
import { Hero } from '../hero';
import { Button } from '../ui';
import { BhajanGrid } from './BhajanGrid';
import { bhajanToCardData } from './BhajanCard';
import type { AnyGraphNode } from '../types/graphNodes';
import { APP } from '../app/appConfig';
import { BROWSE_HREF } from '../browse/facets';
import { AppDownload } from './layout/AppDownload';

/** Deterministic 32-bit hash for a string seed. */
function strHash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return h >>> 0;
}

/** Today, in the user's local timezone, as YYYY-MM-DD. */
function localDateSeed(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const Home: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const catalog = useSelector(selectCatalog);
  const language = useSelector((s: RootState) => s.language.selectedLanguage);

  useEffect(() => { dispatch(fetchBhajanDataRequest()); }, [dispatch]);

  const allNodes = useMemo<AnyGraphNode[]>(
    () => (catalog?.nodes ?? []),
    [catalog]
  );

  /** Single bhajan with complete info (audio + babies + metadata). Stays consistent for the day. */
  const bhajanOfTheDay = useMemo(() => {
    const requireEdges = ['ragam', 'talam', 'deity', 'audible', 'lyrics'];
    const complete = allNodes.filter((n) => {
      if (n.nodeTypeId !== 1) return false;
      const edgeNames = new Set((n.edges ?? []).map((e) => e.name));
      return requireEdges.every((r) => edgeNames.has(r));
    });
    if (complete.length === 0) return null;
    const idx = strHash(localDateSeed()) % complete.length;
    return bhajanToCardData(complete[idx], allNodes);
  }, [allNodes]);

  /**
   * "Today's bhajans" — exactly 8 deterministically chosen bhajans for the day.
   * Renders as a responsive grid: 2 cols × 2-4 rows depending on viewport.
   * Desktop ≥ 1024px shows 4 cols × 2 rows = 8.
   */
  const todays = useMemo(() => {
    const requireEdges = ['ragam', 'talam', 'lyrics'];
    const set = allNodes.filter((n) => {
      if (n.nodeTypeId !== 1) return false;
      const edgeNames = new Set((n.edges ?? []).map((e) => e.name));
      return requireEdges.every((r) => edgeNames.has(r));
    });
    if (set.length === 0) return [];
    const seed = strHash(localDateSeed());
    return Array.from({ length: 8 }, (_, i) => {
      const idx = (seed + i * 1009) % set.length;
      return set[idx];
    }).map((n) => bhajanToCardData(n, allNodes));
  }, [allNodes]);

  const bhajanOfDayHref = APP.features.bhajanOfTheDay && bhajanOfTheDay
    ? `/nodes/${bhajanOfTheDay.nodeId}`
    : '/bhajans';

  return (
    <main id="main">
      <Hero bhajanOfDayHref={bhajanOfDayHref} />

      {APP.features.appDownload && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 pt-8 sm:pt-10">
          <AppDownload />
        </section>
      )}

      {APP.features.todaysBhajans && todays.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 pt-12 sm:pt-16 pb-4">
          <header className="mb-6 flex items-baseline justify-between">
            <h2 className="text-h2 font-display font-semibold">Today's bhajans</h2>
            <span className="text-small text-text-tertiary hidden sm:inline">
              Refreshed daily
            </span>
          </header>
          {/* density=compact -> 1 / 2 / 3 / 4 cols (mobile/tablet/lg/xl). 8 bhajans = 2 rows on tablet+. */}
          <BhajanGrid
            bhajans={todays}
            language={language || 'English'}
            density="compact"
          />
        </section>
      )}

      {/* Promo: project.bhajanmala.com — the live-projection companion site */}
      {APP.features.projectModePromo && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 pt-12 sm:pt-16">
          <a
            href="https://project.bhajanmala.com"
            className="group block rounded-3xl border border-light p-6 sm:p-8 transition hover:border-accent-gold/50"
            style={{
              background:
                'radial-gradient(ellipse at 0% 0%, rgba(245,158,11,0.12), transparent 55%), radial-gradient(ellipse at 100% 100%, rgba(99,102,241,0.10), transparent 55%), var(--bg-1)',
            }}
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
              <div className="flex-1">
                <p className="text-small uppercase tracking-[0.2em] text-accent-gold/80 mb-1">New · for presenters</p>
                <h2 className="text-h2 font-display font-semibold mb-2">Project Mode</h2>
                <p className="text-text-secondary max-w-2xl">
                  Leading a satsang? <span className="text-text-primary">project.bhajanmala.com</span> finds any
                  bhajan in a keystroke and projects the lyrics on the big screen — interleaved in Telugu, Hindi,
                  Kannada, English (IAST) and more, so everyone can sing along.
                </p>
              </div>
              <span className="shrink-0 inline-flex items-center gap-2 rounded-xl bg-accent-gold px-5 py-3 font-semibold text-bg-0 group-hover:gap-3 transition-all">
                Open Project Mode →
              </span>
            </div>
          </a>
        </section>
      )}

      <section className="mx-auto max-w-3xl px-4 sm:px-6 py-16 text-center">
        <h2 className="text-h2 font-display font-semibold mb-3">Browse the full library</h2>
        <p className="text-text-secondary mb-6">
          Search by ragam, talam, deity, or composer.
        </p>
        {/*
          "Search by ragam, talam, deity, or composer" describes /browse, the
          facet hub — not /bhajans, the flat list this used to point at.
          BROWSE_HREF, not a literal, because a tenant with no facet hubs has
          no /browse route to land on. The label is destination-neutral for
          the same reason: "Explore all bhajans" promised the flat list.
        */}
        <Link to={BROWSE_HREF}>
          <Button variant="primary" size="lg">Start browsing</Button>
        </Link>
      </section>
    </main>
  );
};

export default Home;
